import { useIntl, type UserProofOfIdSumsubRequirementConfig, type UserRequirementStateStatus } from '@capsicum/common';
import { Button, FormProvider, Text } from '@capsicum/common-web';
import { type Requirement } from '@capsicum/socios-core';
import { useMemo } from 'react';
import styled from 'styled-components';

import { useSumsubIdentityVerification } from '../../hooks/useSumsubIdentityVerification';
import { SumsubSdkWrapper } from '../sumsub/SumsubSdkWrapper';

import { IdentityVerificationForm } from './IdentityVerificationForm';
import { SumSubConsentPage } from './SumSubConsentPage';
import { SumsubSkeleton } from './SumsubSkeleton';

type SumsubIdentityVerificationProps = {
	isForcedRequirement?: boolean;
	isUniqueRequirement?: boolean;
	onDismiss: () => void;
	requirement: Requirement<UserProofOfIdSumsubRequirementConfig>;
	status?: UserRequirementStateStatus;
};

export const ProofOfIdentitySumsub = ({
	isUniqueRequirement,
	onDismiss,
	requirement,
	status
}: SumsubIdentityVerificationProps) => {
	const { $t } = useIntl();
	const {
		config: { authToken }
	} = requirement;
	const {
		hasConsentedData,
		hideSumsubSdk,
		isLoading,
		isRequirementFulfilled,
		listeners,
		setHasConsentedData,
		sumsubCompletedForm
	} = useSumsubIdentityVerification({
		isUniqueRequirement: Boolean(isUniqueRequirement),
		onDismiss,
		status
	});

	const SumsubContent = useMemo(() => {
		if (isLoading) return <SumsubSkeleton />;
		if (isRequirementFulfilled) {
			return (
				<FormProvider form={sumsubCompletedForm} testID="sumsub-completed-form">
					<Text variant="title5">{$t('kycUserIdentity.yourVerifiedInformation')}</Text>
					<IdentityVerificationForm isFormLoading={false} />
					<Button
						fullWidth
						testID="sumsub-completed-form-back-button"
						text={$t('global.button.back')}
						onClick={onDismiss}
					/>
				</FormProvider>
			);
		}
		if (!hasConsentedData)
			return <SumSubConsentPage onAgree={() => setHasConsentedData(true)} onDismiss={onDismiss} />;

		return <SumsubSdkWrapper authToken={authToken} {...listeners} />;
	}, [
		authToken,
		hasConsentedData,
		isLoading,
		isRequirementFulfilled,
		listeners,
		onDismiss,
		setHasConsentedData,
		sumsubCompletedForm
	]);

	if (hideSumsubSdk) return null;

	return (
		<S.Container>
			<S.Wrapper $isSumsubConsentPage={!hasConsentedData}>{SumsubContent}</S.Wrapper>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		flex: 1;
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 51px`};
		}
	`;

	export const FormTitle = styled(Text)`
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const Wrapper = styled.div<{ $isSumsubConsentPage: boolean }>`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		border-radius: ${({ theme }) => theme.sizing.xxsmall};
		padding: ${({ theme }) => theme.spacings.small};
		min-height: 75dvh;
		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			min-height: 70dvh;
		}
		${({ $isSumsubConsentPage, theme }) => $isSumsubConsentPage && `margin-bottom: ${theme.spacings.xxxlarge};`}
	`;
}
